import { SimpleGrid, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import React, { useEffect, useState } from 'react';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { prepFormValuesForUpsert } from 'src/non-financial-data/prepFormValuesForUpsert';
import { UpsertEntityInput } from 'src/data-capture/merge-review/useUpsertEntities';
import { DynamicUiProviderNew } from '../DynamicUiProviderNew';
import { isFormEditable } from '../dataHelpersNew';
import { getInitialFormData } from '../dynamicUiInitialValuesHelpers';
import { DynamicUiData, FormValuesType, UiItem, UiSection } from '../types';
import DynamicLayoutItem from './DynamicLayoutItem';

type Props = {
  uiData: DynamicUiData;
  items?: UiItem[];
  footer?: React.ReactNode;
  isCreateMode: boolean;
  isDeletable: boolean;
  onDelete?: () => void;
  onSubmit: (clientId: string, upsertEntities: UpsertEntityInput[]) => void;
  numColumns?: number;
  createMode?: boolean;
  renderUiEntityTitle?: (uiSection: UiSection) => React.ReactNode;
};

export default function DynamicFormNew({
  uiData,
  items,
  onSubmit,
  footer = null,
  isCreateMode = false,
  isDeletable,
  numColumns,
  onDelete,
  renderUiEntityTitle,
}: Props) {
  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    ...getInitialFormData(uiData, uiData.ui.layout),
  });
  const [isEditing, setIsEditing] = useState(isCreateMode);
  const renderedItems = items ?? uiData.ui.layout;
  const { clientId } = useCurrentClient();

  useEffect(() => {
    const isDirty = form.isDirty();
    form.setValues(getInitialFormData(uiData, uiData.ui.layout).initialValues);

    // Preserve dirty state after overriding values
    if (!isDirty) {
      form.resetDirty();
    }
  }, [uiData]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (form.isDirty()) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const handleSubmit = async (formValues: FormValuesType) => {
    if (!uiData || !clientId) {
      return;
    }
    const valuesToSubmit = prepFormValuesForUpsert(formValues, uiData, form);
    await onSubmit(clientId, valuesToSubmit);
    form.resetDirty();
    setIsEditing(false);
  };

  const toggleEditing = () => setIsEditing(!isEditing);
  const isEditable = isFormEditable(form, renderedItems, uiData);

  return (
    <form role="form" onSubmit={form.onSubmit(handleSubmit)} style={{ flex: 1 }}>
      <DynamicUiProviderNew
        form={form}
        uiData={uiData}
        items={renderedItems}
        isEditable={isEditable}
        isEditing={isEditing}
        isDeletable={isDeletable}
        onDelete={onDelete}
        isCreateMode={isCreateMode}
        setIsEditing={setIsEditing}
        toggleEditing={toggleEditing}
        renderUiEntityTitle={renderUiEntityTitle}
        clientId={clientId}
      >
        <Wrapper numColumns={numColumns}>
          {renderedItems.map((item, index) => (
            <DynamicLayoutItem key={index} item={item} />
          ))}
        </Wrapper>
        {footer}
      </DynamicUiProviderNew>
    </form>
  );
}

const Wrapper = ({ children, numColumns }: { children: React.ReactNode; numColumns?: number }) => {
  if (numColumns == null) {
    return <Stack>{children}</Stack>;
  }
  return <SimpleGrid cols={numColumns}>{children}</SimpleGrid>;
};
